import ProductOfferAlert from '@/components/Alert/ProductOfferAlert';
import { useDomainOptions } from '@/components/FormDomainSelect';
import { useLicensedGovernanceDomainIds } from '@/http/queries/useFetchPolicyDomainAccess';
import type { PolicyResponse } from '@/http/queries/useFetchPolicies';
import { T } from '@/i18n/T';
import { getUnlicensedDomainLabels } from './governanceDomainOptions';
import { getUnlicensedPolicyDomainIds } from './policyLicenseCoverage';

export function PolicyLicenseWarning({
  policies,
}: {
  policies: PolicyResponse[];
}) {
  const {
    licensedDomainIds,
    organisationDomainIds,
    expiredDomainIds,
    isLicenseUnavailable,
  } = useLicensedGovernanceDomainIds();
  const affectedDomainIds = [
    ...new Set(
      policies.flatMap((policy) =>
        getUnlicensedPolicyDomainIds(
          policy,
          organisationDomainIds,
          licensedDomainIds,
        ),
      ),
    ),
  ];
  const domainOptions = useDomainOptions(affectedDomainIds);

  if (isLicenseUnavailable || affectedDomainIds.length === 0) {
    return null;
  }

  const expired = new Set(expiredDomainIds);
  const domainList = (ids: number[]) =>
    getUnlicensedDomainLabels(ids, domainOptions, licensedDomainIds);
  const lapsedDomains = domainList(
    affectedDomainIds.filter((id) => expired.has(id)),
  );
  const unlicensedDomains = domainList(
    affectedDomainIds.filter((id) => !expired.has(id)),
  );

  return (
    <>
      {lapsedDomains.length > 0 && (
        <ProductOfferAlert
          product={<T k='governance' />}
          variant='error'
          title={<T k='contentPolicyLicenceExpiredTitle' />}
        >
          <T k='contentPolicyLicenceExpiredDescription' />{' '}
          {lapsedDomains.join(', ')}
        </ProductOfferAlert>
      )}
      {unlicensedDomains.length > 0 && (
        <ProductOfferAlert
          product={<T k='governance' />}
          variant='warning'
          title={<T k='contentPolicyLicenceMissingTitle' />}
        >
          <T k='contentPolicyLicenceMissingDescription' />{' '}
          {unlicensedDomains.join(', ')}
        </ProductOfferAlert>
      )}
    </>
  );
}
